import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { COMPONENT_TYPE_MAPPING, useDefaultEditorStyle, WEBSITE_SOURCE_DATA_KEY_MAPPING } from '@bingads-webui-campaign-react/smart-component-util';
import { useSiteConfig } from '@bingads-webui-campaign-react/smart-page-context';
import { DefaultButton, Text, Label } from '@fluentui/react';
import { proxyReadBusinessName } from '@bingads-webui-campaign-react/smart-component-header';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import { proxyReadPrivacyPolicyComponentConfig } from './component-config-reader';
import { EditPrivacyDialog, labelErrorStyle } from './components/edit-privacy-dialog';

const getStyles = (_, { palette = {} }) => ({
  button: {
    color: palette.themePrimary,
  },
});

const UnstyledSmartPrivacyPolicyEditor = ({
  i18n,
  classes,
}) => {
  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const {
    policyContent,
    isPrivacyStatementReviewed,
  } = proxyReadPrivacyPolicyComponentConfig(siteConfig, i18n);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const { businessName, defaultBusinessName } = proxyReadBusinessName(siteConfig, i18n);

  const {
    titleClassName,
    descriptionClassName,
    editorContentClassName,
  } = useDefaultEditorStyle();

  const onSave = useCallback((value) => {
    dispatchSiteConfig({
      type: 'UPDATE_WEBSITESOURCEDATA_BY_NAME',
      payload: {
        name: WEBSITE_SOURCE_DATA_KEY_MAPPING.PrivacyPolicy,
        value,
      },
    });

    dispatchSiteConfig({
      type: 'UPDATE_SITE_CONFIG_BY_TYPE',
      payload: {
        type: COMPONENT_TYPE_MAPPING.Footer,
        value: {
          isPrivacyStatementReviewed: true,
        },
      },
    });
  }, [dispatchSiteConfig]);

  return (
    <div>
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Privacy policy'))}
      </h2>
      <div className={descriptionClassName}>
        <Text block>
          <ReactTemplate
            model={{ bold: text => <b>{text}</b> }}
            template={i18n.getString(_TL_('Create a privacy statement describing how you\'ll use your customers\' data. This statement is legally required. We\'ll provide you with a generic privacy statement that may apply in part to your business; however, it is {{bold}}your responsibility to ensure that the statement is fully accurate.{{/bold}}'))}
          />
        </Text>
        <Text block style={{ marginTop: 5 }}>
          {i18n.getString(_TL_('Please note we\'ll display Microsoft\'s privacy policy alongside the one you create. This is because Microsoft processes your customers\' data before sharing it with you.'))}
        </Text>
      </div>
      <div className={editorContentClassName}>
        <DefaultButton
          className="trigger-edit-privacy-dialog-button"
          onClick={() => setShowEditDialog(true)}
          iconProps={{
            iconName: 'PageEdit',
            className: classes.button,
          }}
          text={i18n.getString(_TL_('Click here to edit your privacy policy'))}
        />
        {
          !isPrivacyStatementReviewed &&
          <Label styles={labelErrorStyle} className="review-policy-error-msg">
            {i18n.getString(_TL_('Please confirm you have reviewed your privacy policy'))}
          </Label>
        }
      </div>
      {
        showEditDialog &&
        <EditPrivacyDialog
          i18n={i18n}
          onSave={onSave}
          onClose={() => setShowEditDialog(false)}
          defaultValue={policyContent}
          isPrivacyStatementReviewed={isPrivacyStatementReviewed}
          businessName={businessName || defaultBusinessName}
        />
      }
    </div>
  );
};

UnstyledSmartPrivacyPolicyEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
};

export const SmartPrivacyPolicyEditor = withDefaultStyles(
  UnstyledSmartPrivacyPolicyEditor,
  getStyles
);
